/**
 * CompanyQuickActions — dropdown menu di azioni rapide per riga azienda.
 *
 * Azioni (admin SUPER-MVP M2):
 *  1. Apri detail              (link a /admin/customers/:id)
 *  2. Modifica dettagli        (drawer scivolante EditCompanyDrawer)
 *  3. Cambia piano             (dialog custom con select piano + reason)
 *  4. Estendi trial            (ReasonConfirmDialog + extraField numero giorni)
 *  5. Sospendi / Riattiva      (ReasonConfirmDialog toggle in base a isActive)
 *  6. Elimina (soft)           (ReasonConfirmDialog destructive)
 *
 * Permanent delete è esclusa di proposito dal menu rapido (anti fat-finger):
 * resta accessibile solo dalla detail page con PermanentDeleteDialog.
 *
 * onUpdated callback viene invocato dopo ogni mutation per refresh della
 * row/lista nel parent (AdminCustomersPage).
 */
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MoreHorizontal,
  Pencil,
  CreditCard,
  Clock,
  Pause,
  Play,
  Trash2,
  ExternalLink,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { adminApi, type AdminCustomerRow, type AdminPlanRow } from '@/services/adminApi';

import { ReasonConfirmDialog } from './ReasonConfirmDialog';
import { PermanentDeleteDialog } from './PermanentDeleteDialog';
import { EditCompanyDrawer } from './EditCompanyDrawer';

type DialogKind =
  | { kind: 'none' }
  | { kind: 'edit' }
  | { kind: 'change-plan' }
  | { kind: 'extend-trial' }
  | { kind: 'suspend' }
  | { kind: 'unsuspend' }
  | { kind: 'soft-delete' }
  | { kind: 'permanent-delete' };

export interface CompanyQuickActionsProps {
  row: AdminCustomerRow;
  onUpdated?: () => void;
}

export function CompanyQuickActions({ row, onUpdated }: CompanyQuickActionsProps) {
  const navigate = useNavigate();
  const [dialog, setDialog] = useState<DialogKind>({ kind: 'none' });
  const [trialDays, setTrialDays] = useState(7);

  // For change-plan dialog
  const [plans, setPlans] = useState<AdminPlanRow[] | null>(null);
  const [selectedPlanId, setSelectedPlanId] = useState<string>('');

  // Lazy-load plans only when change-plan dialog opens
  useEffect(() => {
    if (dialog.kind !== 'change-plan' || plans !== null) return;
    (async () => {
      try {
        const res = await adminApi.plans.list({});
        setPlans(res.items);
      } catch (err) {
        toast.error(`Impossibile caricare i piani: ${(err as Error).message}`);
      }
    })();
  }, [dialog.kind, plans]);

  const close = () => setDialog({ kind: 'none' });

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
            <MoreHorizontal className="h-4 w-4" />
            <span className="sr-only">Azioni</span>
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56" onClick={(e) => e.stopPropagation()}>
          <DropdownMenuLabel className="text-xs text-muted-foreground font-normal">
            {row.publicName || row.name}
          </DropdownMenuLabel>
          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => navigate(`/admin/customers/${row.id}`)}>
            <ExternalLink className="h-3.5 w-3.5 mr-2" />
            Apri dettaglio
          </DropdownMenuItem>

          <DropdownMenuItem onClick={() => setDialog({ kind: 'edit' })}>
            <Pencil className="h-3.5 w-3.5 mr-2" />
            Modifica dettagli
          </DropdownMenuItem>

          <DropdownMenuSeparator />

          <DropdownMenuItem onClick={() => { setSelectedPlanId(row.planId ?? ''); setDialog({ kind: 'change-plan' }); }}>
            <CreditCard className="h-3.5 w-3.5 mr-2" />
            Cambia piano
          </DropdownMenuItem>

          {row.trialStatus === 'active' && (
            <DropdownMenuItem onClick={() => setDialog({ kind: 'extend-trial' })}>
              <Clock className="h-3.5 w-3.5 mr-2" />
              Estendi trial
            </DropdownMenuItem>
          )}

          {row.isActive ? (
            <DropdownMenuItem onClick={() => setDialog({ kind: 'suspend' })}>
              <Pause className="h-3.5 w-3.5 mr-2" />
              Sospendi
            </DropdownMenuItem>
          ) : (
            <DropdownMenuItem onClick={() => setDialog({ kind: 'unsuspend' })}>
              <Play className="h-3.5 w-3.5 mr-2" />
              Riattiva
            </DropdownMenuItem>
          )}

          <DropdownMenuSeparator />

          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => setDialog({ kind: 'soft-delete' })}
          >
            <Trash2 className="h-3.5 w-3.5 mr-2" />
            Elimina (soft)
          </DropdownMenuItem>

          <DropdownMenuItem
            className="text-destructive focus:text-destructive"
            onClick={() => setDialog({ kind: 'permanent-delete' })}
          >
            <Trash2 className="h-3.5 w-3.5 mr-2" />
            Elimina (hard)
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      {/* Edit drawer */}
      <EditCompanyDrawer
        open={dialog.kind === 'edit'}
        onOpenChange={(o) => !o && close()}
        companyId={dialog.kind === 'edit' ? row.id : null}
        onSaved={onUpdated}
      />

      {/* Change plan custom dialog */}
      <AlertDialog open={dialog.kind === 'change-plan'} onOpenChange={(o) => !o && close()}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <CreditCard className="h-4 w-4" />
              Cambia piano — {row.publicName || row.name}
            </AlertDialogTitle>
            <AlertDialogDescription>
              Piano attuale: <code className="text-xs">{row.planId ?? '—'}</code>. Il nuovo piano sarà applicato sia su Autumn (Stripe) che su Firestore. Audit log registrerà il cambio.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="space-y-3 py-2">
            <div className="space-y-1.5">
              <Label htmlFor="new-plan">Nuovo piano</Label>
              {plans === null ? (
                <div className="h-9 flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Caricamento piani...
                </div>
              ) : (
                <Select value={selectedPlanId} onValueChange={setSelectedPlanId}>
                  <SelectTrigger id="new-plan">
                    <SelectValue placeholder="Seleziona piano" />
                  </SelectTrigger>
                  <SelectContent>
                    {plans
                      .filter((p) => !p.deprecated)
                      .map((p) => (
                        <SelectItem key={p.planId} value={p.planId}>
                          {p.planId} {p.priceMonthly ? `(€${p.priceMonthly}/mo)` : ''}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <ChangePlanReasonField
              onSubmit={async (reason) => {
                if (!selectedPlanId) {
                  toast.error('Seleziona un piano prima');
                  return;
                }
                if (selectedPlanId === row.planId) {
                  toast.error('Il piano selezionato è già quello attuale');
                  return;
                }
                try {
                  const result = await adminApi.subscription.changePlan(row.id, selectedPlanId, reason);
                  if (result.warning) {
                    toast.warning(`Plan applicato con warning: ${result.warning}`);
                  } else {
                    toast.success(`Piano cambiato a ${result.newPlanId}`);
                  }
                  onUpdated?.();
                  close();
                } catch (err) {
                  toast.error((err as Error).message);
                  throw err;
                }
              }}
              disabled={!selectedPlanId || selectedPlanId === row.planId}
            />
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Annulla</AlertDialogCancel>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Extend trial — ReasonConfirmDialog with extra days field */}
      <ReasonConfirmDialog
        open={dialog.kind === 'extend-trial'}
        onOpenChange={(o) => !o && close()}
        title={`Estendi trial — ${row.publicName || row.name}`}
        description={`Trial attuale: ${row.trialEndsAt ? `scade il ${new Date(row.trialEndsAt).toLocaleDateString('it-IT')}` : 'in corso'}. Aggiungi giorni alla scadenza.`}
        confirmLabel={`Estendi di ${trialDays}gg`}
        extraFields={
          <div className="space-y-1.5">
            <Label htmlFor="trial-days">Giorni da aggiungere</Label>
            <Input
              id="trial-days"
              type="number"
              min={1}
              max={365}
              value={trialDays}
              onChange={(e) => setTrialDays(Math.max(1, Number(e.target.value) || 7))}
            />
          </div>
        }
        onConfirm={async (reason) => {
          await adminApi.subscription.extendTrial(row.id, trialDays, reason);
          toast.success(`Trial esteso di ${trialDays} giorni`);
          onUpdated?.();
        }}
      />

      {/* Suspend */}
      <ReasonConfirmDialog
        open={dialog.kind === 'suspend'}
        onOpenChange={(o) => !o && close()}
        title={`Sospendi — ${row.publicName || row.name}`}
        description="L'azienda non potrà accedere alla piattaforma finché non viene riattivata. I dati non vengono cancellati."
        confirmLabel="Sospendi"
        destructive
        onConfirm={async (reason) => {
          await adminApi.customers.suspend(row.id, reason);
          toast.success('Azienda sospesa');
          onUpdated?.();
        }}
      />

      {/* Unsuspend */}
      <ReasonConfirmDialog
        open={dialog.kind === 'unsuspend'}
        onOpenChange={(o) => !o && close()}
        title={`Riattiva — ${row.publicName || row.name}`}
        description="L'azienda potrà accedere nuovamente alla piattaforma."
        confirmLabel="Riattiva"
        onConfirm={async (reason) => {
          await adminApi.customers.unsuspend(row.id, reason);
          toast.success('Azienda riattivata');
          onUpdated?.();
        }}
      />

      {/* Soft delete */}
      <ReasonConfirmDialog
        open={dialog.kind === 'soft-delete'}
        onOpenChange={(o) => !o && close()}
        title={`Elimina (soft) — ${row.publicName || row.name}`}
        description="Soft delete: l'azienda viene marcata come eliminata. I dati sono preservati e recuperabili via gcloud restore. Per cancellazione definitiva irreversibile, usa 'Elimina (hard)'."
        confirmLabel="Elimina (soft)"
        destructive
        onConfirm={async (reason) => {
          await adminApi.customers.softDelete(row.id, reason);
          toast.success('Azienda eliminata (soft)');
          onUpdated?.();
        }}
      />

      {/* Permanent delete — typing del companyId richiesto (anti fat-finger).
          Endpoint BE: DELETE /v1/admin/customers/:companyId/permanent con body
          { reason, confirm: companyId }. Effetti: cancella companies/{cid} doc
          + best-effort cleanup memberships. Tech debt: subcollections (members,
          teams, jobPositions, ecc.) restano orphaned (vedi commento in
          CompanyService.permanentDeleteCompany).
          Fix BUG-PD (commit a9d7d94 stamattina) garantisce che il sudo possa
          cancellare anche company di cui NON è member ADMIN. */}
      <PermanentDeleteDialog
        open={dialog.kind === 'permanent-delete'}
        onOpenChange={(o) => !o && close()}
        entityKind={`azienda ${row.publicName || row.name}`}
        expectedName={row.id}
        onConfirm={async (reason) => {
          await adminApi.customers.permanentDelete(row.id, reason, row.id);
          toast.success(`Azienda ${row.publicName || row.name} eliminata definitivamente`);
          onUpdated?.();
        }}
      />
    </>
  );
}

/**
 * Sub-component for change-plan: gestisce reason input + submit button
 * separato perché AlertDialog non supporta nativamente il pattern
 * extraFields+reason+confirm. Custom-tailored per change-plan.
 */
function ChangePlanReasonField({
  onSubmit,
  disabled,
}: {
  onSubmit: (reason: string) => Promise<void>;
  disabled: boolean;
}) {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim() || disabled || busy) return;
    setBusy(true);
    try {
      await onSubmit(reason.trim());
    } catch {
      // toast handled upstream
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-1.5">
      <Label htmlFor="change-plan-reason">Reason (richiesto)</Label>
      <Input
        id="change-plan-reason"
        placeholder="Es. richiesta cliente, upgrade commerciale..."
        value={reason}
        onChange={(e) => setReason(e.target.value)}
        disabled={busy}
      />
      <div className="pt-2">
        <Button
          className="w-full"
          onClick={submit}
          disabled={!reason.trim() || disabled || busy}
        >
          {busy && <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" />}
          Applica cambio piano
        </Button>
      </div>
    </div>
  );
}
